import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertController, ModalController, NavController, PopoverController } from '@ionic/angular';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';

import { Customer, Outbound, Template } from 'src/models';
import { CHANNEL_TYPES } from 'src/models/channels';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { CustomersService } from 'src/services/customers.service';
import { MessageService } from 'src/services/message.service';
import { TagsService } from 'src/services/tags.service';

import { ActionDropdownComponent, ActionDropdownType } from '../components/action-dropdown/action-dropdown.component';
import { MessageAnalyticsPage } from '../components/messaging/message-analytics/message-analytics.page';
import {
  Block,
  BLOCK_TYPES,
  htmlToBlocks,
  isHeader,
  isRichText,
  isSection,
  renderBlocks,
  SendAs,
} from '../components/slack-blocks/common';
import {
  FEATURES,
  OUTBOUND_LEVEL,
  OUTBOUND_RECIPIENT_TYPE,
  OUTBOUND_SENDER_TYPE,
  OUTBOUND_STATUS,
  routes,
  TEMPLATE_TYPE,
} from '../constants';
import { SelectedFilterMap } from '../utils/filter-utils';
import { createUserRecipient, getWhereClausesFromFilters } from '../utils/utils';

@Component({
  selector: 'app-slack-message',
  templateUrl: './slack-message.page.html',
  styleUrls: ['./slack-message.page.scss'],
})
export class SlackMessagePage implements OnInit {
  @ViewChild('slackBlocks') slackBlocks;

  isLoading: boolean = true;
  isLoadingCustomers: boolean = false;
  isSaving: boolean = false;
  isSendingTest: boolean = false;
  customerIds?: string[] = [];
  cloneId?: string;
  outboundId?: string;
  isCreating: boolean = false;
  isSent: boolean = false;
  subject: string;
  blocks: Block[] = [];
  sendAs: SendAs | null = null;
  outbound: any;
  selectedTab: 'message' | 'recipients' | 'send' = 'message';
  previousUrl?: string;
  isScheduled: boolean = false;
  runAt?: moment.Moment;
  initialRunAt?: moment.Moment;
  recipients: any[] = [];
  allCustomers: Partial<Customer>[] = [];
  filteredCustomers: Partial<Customer>[] = [];
  showArchivedCustomers: boolean;
  public readonly customers$: BehaviorSubject<Partial<Customer>[]> = new BehaviorSubject([]);
  recipientOptions: any[];
  sendDirectMessages = false;
  maxDate: Date;
  searchText: string;
  sendAsType: string = OUTBOUND_SENDER_TYPE.SELF;
  unfurlLinks = false;

  filters: SelectedFilterMap = new Map();
  filterOptions = [
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      id: 'tags.id',
      icon: 'pricetag',
      type: 'select',
      values: async () => {
        const tags = await this.tagsService.list({
          limit: null,
          order: ['name'],
          descending: false,
        });

        const result = tags.data.map((tag) => {
          return {
            label: tag.name,
            id: tag.id,
          };
        });

        return result;
      },
    },
    {
      label: 'Date created',
      id: 'createdAt',
      icon: 'calendar',
      type: 'date',
    },
  ];

  featureEnabled = true;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private navController: NavController,
    private popoverCtrl: PopoverController,
    private alertCtrl: AlertController,
    private apiService: ApiService,
    public authService: AuthService,
    private customersService: CustomersService,
    private msgSrvc: MessageService,
    private modalCtrl: ModalController,
    private analyticsService: AnalyticsService,
    private tagsService: TagsService,
  ) {}

  async ngOnInit() {
    const outboundId = this.route.snapshot.paramMap.get('outboundId');
    this.previousUrl = this.route.snapshot.queryParamMap.get('previousUrl');
    this.cloneId = this.route.snapshot.queryParamMap.get('cloneId');
    this.isCreating = outboundId === 'new';

    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.OUTBOUND_SLACK);

    if (!this.isCreating) {
      this.outboundId = outboundId;

      const outbound = await this.loadSlackMessage(this.outboundId);

      this.outbound = outbound;

      if (!this.outbound.sendToSlackChannel) {
        this.sendDirectMessages = true;
      }

      this.recipients = this.outbound.recipients ?? [];
      this.isSent = outbound.status === OUTBOUND_STATUS.SENT || outbound.status === OUTBOUND_STATUS.FAILED;
    } else if (this.cloneId) {
      await this.loadSlackMessage(this.cloneId);

      // Prevent scheduling an outbound to send in the past
      if (moment(this.initialRunAt).isBefore(moment())) {
        this.initialRunAt = moment();
        this.isScheduled = false;
      }
    } else {
      this.customerIds = this.route.snapshot.queryParamMap.get('customerIds')?.split(',') ?? [];

      this.sendAs = {
        type: OUTBOUND_RECIPIENT_TYPE.USER,
        id: this.authService.userId,
      };

      const templateId = this.route.snapshot.queryParamMap.get('templateId');
      const surveyId = this.route.snapshot.queryParamMap.get('surveyId');

      if (templateId) {
        this.loadTemplate(templateId);
      } else if (surveyId) {
        this.loadSurvey(surveyId);
      }
    }

    if (!this.initialRunAt) {
      this.initialRunAt = moment().add(1, 'hours');
    }

    this.maxDate = moment().add(29, 'days').toDate();

    this.isLoading = false;

    await this.loadCustomers();
  }

  async loadSlackMessage(outboundId: string) {
    const outbound = (await this.apiService.getPromise(`/outbounds/${outboundId}`)) as Outbound;

    outbound.blocks = htmlToBlocks(outbound.body);

    this.subject = outbound.subject;
    this.blocks = outbound.blocks;
    this.sendAs = outbound.sendAs;
    this.recipients = outbound.recipients;

    if (outbound.runAt) {
      this.initialRunAt = moment(outbound.runAt);
      this.isScheduled = true;
    }

    if (outbound.metadata) {
      this.unfurlLinks = outbound.metadata.unfurlLinks ?? false;
    }

    return outbound;
  }

  async loadTemplate(templateId: string) {
    const template = (await this.apiService.getPromise(`/templates/${templateId}`)) as Template;

    this.blocks = htmlToBlocks(template.html);
  }

  async loadSurvey(surveyId: string) {
    const survey = (await this.apiService.getPromise(`/surveys/${surveyId}`)) as any;

    if (survey) {
      this.blocks.push({
        type: BLOCK_TYPES.SURVEY,
        elements: [],
        survey: {
          ...survey.style,
          type: survey.type,
        },
      });
    }
  }

  nextStep() {
    if (this.selectedTab === 'message') {
      this.selectedTab = 'recipients';
    } else if (this.selectedTab === 'recipients') {
      this.selectedTab = 'send';
    }

    if (!this.isSent) {
      this.saveAsDraft();
    }
  }

  async showAnalytics() {
    this.analyticsService.trackEvent('Slack Message', 'View message analytics', {
      outboundId: this.outboundId,
      deliveryMethod: this.outbound.deliveryMethod,
    });

    const modal = await this.modalCtrl.create({
      component: MessageAnalyticsPage,
      componentProps: {
        outbound: this.outbound,
      },
      cssClass: 'modal-large',
    });

    modal.present();
  }

  async showOptions() {
    const SAVE_DRAFT = this.isSent ? 'Clone' : `Save ${this.outboundId ? 'changes' : 'as draft'}`;
    const SAVE_TEMPLATE = 'Save as a new template';
    const DISCARD_DRAFT = 'Discard draft';
    const UNSEND_MESSAGE = 'Unsend messages';

    const icon = this.isSent ? 'duplicate-outline' : 'create-outline';
    const options: ActionDropdownType[] = [
      {
        label: SAVE_DRAFT,
        icon,
      },
      {
        label: SAVE_TEMPLATE,
        icon: 'shapes-outline',
      },
    ];

    if (this.outboundId && !this.isSent) {
      options.push({
        label: DISCARD_DRAFT,
        icon: 'trash-outline',
        destructive: true,
      });
    }

    if (this.outboundId && this.isSent) {
      options.push({
        label: UNSEND_MESSAGE,
        icon: 'trash-outline',
        destructive: true,
      });
    }

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: (_index: number, label: string) => {
          if (label === SAVE_DRAFT) {
            if (this.isSent) {
              this.duplicate();
            } else {
              this.saveAsDraft();
            }
          } else if (label === DISCARD_DRAFT) {
            this.deleteDraft();
          } else if (label === SAVE_TEMPLATE) {
            this.saveTemplate();
          } else if (label === UNSEND_MESSAGE) {
            this.unsendMessages();
          }

          popover.dismiss();
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  async saveTemplate() {
    this.analyticsService.trackEvent('Slack Message', 'Save Slack Message as Template');

    const confirm = await this.alertCtrl.create({
      header: `Save as new template`,
      inputs: [
        {
          placeholder: 'Enter name',
          attributes: {
            maxLength: 320,
          },
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Save',
          handler: async (data: any[]) => {
            let name = data[0];

            if (!name) {
              return;
            }

            const request = this.apiService.post(`/templates`, {
              name,
              type: TEMPLATE_TYPE.SLACK,
              isDiscoverable: true,
              html: renderBlocks(this.blocks),
              design: null,
              communityId: null,
            });

            this.isLoading = true;
            request.subscribe(
              async (result) => {
                this.msgSrvc.show('✔ Nice! You can find this in the "Templates" tab.');

                this.isLoading = false;
              },
              async (err) => {
                this.msgSrvc.showError(err);

                this.isLoading = false;
              },
              () => {
                this.isLoading = false;
              },
            );
          },
        },
      ],
    });
    confirm.present();
  }

  async duplicate() {
    this.analyticsService.trackEvent('Slack Message', 'Duplicate Slack Message');

    await this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: {
        previousUrl: this.previousUrl,
        cloneId: this.outboundId,
      },
    });
  }

  async send() {
    await this.save();
    this.analyticsService.trackEvent('Slack Message', 'Send Slack message');
  }

  async saveAsDraft() {
    await this.save(true);
  }

  async deleteDraft() {
    try {
      this.isLoading = true;

      await this.apiService.deletePromise(`/outbounds/${this.outboundId}`);

      this.dismiss();
    } catch (err) {
      this.isLoading = false;
      this.msgSrvc.showError(err);
    }
  }

  async unsendMessages() {
    try {
      this.isLoading = true;

      await this.apiService.postPromise(`/outbounds/${this.outboundId}/unsend`, {});

      this.dismiss();
    } catch (err) {
      this.isLoading = false;
      this.msgSrvc.showError(err);
    }
  }

  getRecipients(isTest: boolean = false) {
    if (isTest) {
      return [createUserRecipient(this.authService.profile)];
    }

    return this.recipients;
  }

  sendTest() {
    this.save(false, true);
  }

  async save(isDraft = false, isTest = false) {
    this.isSaving = !isTest;
    this.isSendingTest = isTest;

    try {
      if (this.isCreating || this.isSent || isTest) {
        const outbound = (await this.apiService.postPromise(`/outbounds`, {
          deliveryMethod: 'slack',
          subject: this.subject || this.getBackupSubject(),
          body: this.slackBlocks.htmlContent,
          creatorName: this.authService.user.displayName,
          communityId: null,
          status: isDraft ? OUTBOUND_STATUS.DRAFT : OUTBOUND_STATUS.SCHEDULED,
          isDraft,
          isTest,
          level: OUTBOUND_LEVEL.TENANT,
          runAt: this.isScheduled && this.runAt ? this.runAt.toDate() : undefined,
          sendToSlackChannel: isTest ? false : true,
          recipients: this.getRecipients(isTest),
          sendAs: this.sendAs ?? null,
          metadata: {
            unfurlLinks: this.unfurlLinks,
          },
          projectId: this.authService.selectedProject?.id,
        })) as any;

        if (!isTest) {
          this.outbound = outbound;
          this.outboundId = outbound.id;
          this.isCreating = false;

          this.location.replaceState(`/${routes.SLACK_MESSAGE}/${this.outboundId}`, window.location.search);
        }
      } else {
        this.outbound = await this.apiService.putPromise(`/outbounds/${this.outboundId}`, {
          subject: this.subject || this.getBackupSubject(),
          body: this.slackBlocks.htmlContent,
          status: isDraft ? OUTBOUND_STATUS.DRAFT : OUTBOUND_STATUS.SCHEDULED,
          isTest,
          runAt: this.isScheduled ? this.runAt?.toDate() : undefined,
          sendAs: this.sendAs ?? null,
          sendToSlackChannel: isTest ? false : true,
          recipients: this.getRecipients(isTest),
          metadata: {
            unfurlLinks: this.unfurlLinks,
          },
        });
      }

      this.isSaving = false;
      this.isSendingTest = false;

      if (isDraft) {
        //// Saved a draft
        // No longer need a notification, we show a label of last updated date
        // this.msgSrvc.show('✓ Draft saved');
      } else if (isTest) {
        //// Sent a test message to yourself
        this.msgSrvc.show('We sent a test message to you via Slack');
      } else {
        //// Real deal; we sent that shit
        this.dismiss();
      }
    } catch (err) {
      this.msgSrvc.showError(err);
      this.isSaving = false;
      this.isSendingTest = false;
    }
  }

  dismiss() {
    this.previousUrl ? this.navController.navigateBack(this.previousUrl) : this.navController.back();
  }

  getBackupSubject() {
    const firstBlock: any = this.blocks.find((block) => isRichText(block) || isSection(block) || isHeader(block));

    if (!firstBlock) {
      return '';
    }

    if ('text' in firstBlock) {
      return firstBlock?.text?.text ?? '';
    }

    const span = document.createElement('span');

    span.innerHTML = firstBlock.htmlContent;

    return span.textContent || span.innerText;
  }

  blocksChanged() {}

  sendAsChanged(sendAs: SendAs | null) {
    this.sendAs = sendAs;
  }

  getInitialDate() {
    return this.initialRunAt.format('YYYY-MM-DD');
  }

  getInitialTime() {
    return this.initialRunAt.format('HH:mm');
  }

  setRunAt([date, time]) {
    this.runAt = moment(`${date} ${time}`);
  }

  async loadCustomers() {
    this.isLoadingCustomers = true;

    try {
      const where = getWhereClausesFromFilters(this.filters);

      if (!this.showArchivedCustomers) {
        where.push({
          field: 'slackChannel.archivedAt',
          operator: '==',
          value: null,
        });
      }

      where.push({
        field: 'type',
        operator: '==',
        value: CHANNEL_TYPES.SLACK,
      });

      if (this.authService.selectedProject) {
        where.push({
          field: 'projectId',
          operator: '==',
          value: this.authService.selectedProject.id,
        });
      }

      where.push({
        field: 'name',
        operator: '!=',
        value: null,
      });

      const customers = await this.customersService.list({
        limit: null,
        order: ['name'],
        descending: false,
        where: where.length > 0 ? where : undefined,
      });

      const tmpCustomerMap = {};
      this.allCustomers = [];
      customers.data.forEach((customer) => {
        if (customer.slackChannelId) {
          tmpCustomerMap[customer.id] = customer;
          this.allCustomers.push(customer);
        }
      });

      this.filteredCustomers = this.allCustomers;
      this.customers$.next(this.allCustomers);

      if (this.customerIds?.length) {
        if (this.customerIds[0] === 'all') {
          this.recipients = this.allCustomers;
        } else {
          this.customerIds.forEach((cId) => {
            const customer = tmpCustomerMap[cId];
            if (customer) {
              this.recipients.push(customer);
            }
          });
        }
      }
    } finally {
      this.isLoadingCustomers = false;
    }
  }

  handleFilters() {
    this.filteredCustomers = this.allCustomers;

    const term = this.searchText?.trim().toLowerCase();
    if (term) {
      this.filteredCustomers = this.filteredCustomers.filter((customer) => customer.name.toLowerCase().includes(term));
    }
  }

  async filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;

    await this.loadCustomers();
  }

  onSelect({ selected }) {
    this.recipients.splice(0, this.recipients.length);
    this.recipients.push(...selected);
  }

  trackRecipientsByFn(item: any) {
    return item.id ?? item.name;
  }

  getDraftSaveDateDisplay() {
    return this.outbound?.updatedAt ? `Last saved ${moment(this.outbound.updatedAt).fromNow()}` : '';
  }

  toggleRowSelected(row: any) {
    if (this.recipients.find((r) => r.id === row.id)) {
      this.recipients = this.recipients.filter((r) => r.id !== row.id);
    } else {
      this.recipients.push(row);
    }
  }
}
